import {
  Contract,
  CategorizedBenefits,
  Eligibilities,
  Entitlement,
  LSCommon,
} from '@legalshield/legalshield-member-react-common';
import { BenefitPage } from '@legalshield/products-ux-framework';
import '@legalshield/products-ux-framework/dist/index.css';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { contractUrlWithId, getSupplementTitle, interpolate } from '../../utils';
import { useLanguage } from '../../hooks/useLanguage';
import ErrorCard from '../ErrorCard';
import Loading from '../Loading';
import { ContractInfo } from '@legalshield/products-ux-framework/dist/components/BenefitPage/BenefitPage.types';

const EntitlementBenefit = (): React.ReactElement => {
  const { entitlementID } = useParams<{ entitlementID: string }>();
  const language = useLanguage();

  const [entitlement, setEntitlement] = useState<Entitlement | undefined>(undefined);
  const [eligibilities, setEligibilities] = useState<Eligibilities | undefined>(undefined);
  const [categories, setCategories] = useState<CategorizedBenefits[] | undefined>(undefined);
  const [error, setError] = useState<AxiosError | undefined>(undefined);
  const title = eligibilities?.productBenefits?.friendlyName ?? string_table.GENERIC_BENEFIT_FRIENDLY_NAME;

  useEffect(() => {
    LSCommon.getInstance().entitlements().fetchEntitlement(entitlementID).then(setEntitlement).catch(setError);
  }, [entitlementID]);

  useEffect(() => {
    if (entitlement) {
      LSCommon.getInstance().benefits().fetchBenefits(entitlement).then(setEligibilities).catch(setError);
    }
  }, [entitlement]);

  useEffect(() => {
    if (eligibilities) {
      setCategories(LSCommon.getInstance().benefits().categorizeBenefits(eligibilities));
    }
  }, [eligibilities]);

  if (error) {
    return <ErrorCard message={error.message} />;
  }

  const getPlanContracts = (): ContractInfo[] | undefined => {
    // Choose the most recent non-supplement contract out of the entitlement contracts
    const planContract = entitlement?.contracts
      ?.filter((c: Contract) => !c.isSupplement)
      ?.reduce((latest: Contract | undefined, current: Contract) => {
        if (!latest) return current;
        return new Date(current.contractDate) > new Date(latest.contractDate) ? current : latest;
      }, undefined);

    if (!planContract) {
      return undefined;
    }

    // Push the contract and the overview if they exist
    const planContracts: ContractInfo[] = [];
    const planTitle = eligibilities?.productBenefits?.friendlyName
      ? interpolate(string_table.BENEFITS_PAGE_VIEW_CONTRACT_TITLE, eligibilities?.productBenefits?.friendlyName)
      : string_table.GENERIC_BENEFIT_FRIENDLY_NAME;
    planContracts.push({
      name: planTitle,
      url: contractUrlWithId(planContract.id),
    });

    if (planContract.productOverview) {
      planContracts.push({
        name: string_table.BENEFITS_PAGE_PLAN_OVERVIEW_LINK_TITLE,
        url: contractUrlWithId(planContract.productOverview),
      });
    }

    return planContracts;
  };

  const getSupplementContracts = (): ContractInfo[] | undefined => {
    const contractDate = entitlement?.contracts?.reduce((latest: Contract | undefined, current: Contract) => {
      if (!latest) return current;
      return new Date(current.contractDate) > new Date(latest.contractDate) ? current : latest;
    }, undefined)?.contractDate;
    const supplementContracts = entitlement?.contracts?.filter(
      (c: Contract) => c.isSupplement && c.contractDate === contractDate,
    );

    return supplementContracts?.map((contract: Contract, index: number) => ({
      name:
        eligibilities?.supplementBenefits.length === 1
          ? interpolate(
              string_table.BENEFITS_PAGE_VIEW_CONTRACT_TITLE,
              eligibilities.supplementBenefits[0].friendlyName,
            )
          : interpolate(string_table.BENEFITS_PAGE_SUPPLEMENT_LINK_TITLE, (index + 1).toString()),
      url: contractUrlWithId(contract.id),
    }));
  };

  const getAllProductContracts = (): ContractInfo[] => {
    const productContracts = getPlanContracts();
    const supplementContracts = getSupplementContracts();
    if (productContracts?.length === 0 && supplementContracts?.length === 0) {
      return [];
    }
    let allContracts: ContractInfo[] = [];
    if (productContracts && productContracts?.length > 0) {
      allContracts = allContracts.concat(productContracts);
    }
    if (supplementContracts && supplementContracts?.length > 0) {
      allContracts = allContracts.concat(supplementContracts);
    }
    return allContracts;
  };

  const onContractPressed = (contractInfo: ContractInfo): void => {
    if (contractInfo.url) {
      window.open(contractInfo.url);
    }
  };

  if (entitlement && eligibilities) {
    return (
      <BenefitPage
        categories={categories}
        title={title}
        supplementTitle={getSupplementTitle(eligibilities)}
        contracts={getAllProductContracts()}
        onPressContract={onContractPressed}
        language={language}
      />
    );
  }

  return <Loading />;
};

export default EntitlementBenefit;
